/**
 * DiscoverPage — the landing page for orgless users, and a browsable
 * catalogue of public projects for everyone else.
 *
 * Loads /api/discover/ paginated. Each card renders the project title,
 * short description, org name, task-count + contributor-count, and a
 * primary action:
 *   - "Join" if the current user is not already joined and the project
 *     accepts open contribution.
 *   - "Open" if already a contributor OR already an org member.
 *   - "View" (disabled) if visibility=public but contribution_mode=
 *     members_only and the caller isn't a member.
 *
 * See native/CROWDSOURCE_ARCHITECTURE.md for the design rationale.
 */
import { useEffect, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { format } from "date-fns";
import { useAPI, useUpdatePageTitle } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { Button, Message, Tooltip } from "@humansignal/ui";
import { Pagination } from "../../components";
import { spaNavigate } from "../../utils/navigation";
import { cn } from "../../utils/bem";
import { JoinProjectModal } from "./JoinProjectModal";
import { LogoThumb } from "../Home/OrglessHome";
import { useTranslation } from "../../i18n/LocaleProvider";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { IconDocument, IconGlobe, IconPeople } from "@humansignal/icons";
import { LeaveProjectModal } from "./LeaveProjectModal";
import type { Page } from "../types/Page";
// Reuse the Projects page card + grid styles so Discover reads as the
// same surface (same card shape, grid, header rhythm). Discover-specific
// bits (join button, org/contributor line) layer on top via the same
// ``project-card`` element classes.
import "../Projects/Projects.prefix.css";
import "./DiscoverPage.css";

type DiscoverProject = {
  id: number;
  title: string | null;
  description: string | null;
  created_at: string;
  visibility: "private" | "public";
  contribution_mode: "members_only" | "open";
  organization: { id: number; title: string } | null;
  task_count: number;
  contributor_count: number;
  rate_card?: { currency: string; rates: Record<string, number> } | null;
  max_contributors: number | null;
  already_joined: boolean;
  already_member: boolean;
  join_status?: "none" | "active" | "pending" | "member";
  logo?: string;
  consent_requirements?: Array<{
    requirement_id: number;
    template_id: number;
    title: string;
    kind?: string;
    body: string;
    version: number;
    purposes: Array<{ key: string; label?: string; description?: string; required?: boolean }>;
    optional: boolean;
  }>;
};

type DiscoverResponse = {
  count?: number;
  next?: string | null;
  results?: DiscoverProject[];
};

const PAGE_SIZE = 20;

/** Per-language public progress strip (community-speech plan, Phase D):
 *  "how far has my language come" — clips collected + crowd-validated
 *  across public projects. Hidden when there's nothing to show. */
const LanguageProgressStrip = () => {
  const { t } = useTranslation();
  const [langs, setLangs] = useState<Array<{
    language: string;
    projects: number;
    clips: number;
    validated: number;
    hours?: number;
  }> | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/languages/progress/", { credentials: "same-origin", cache: "no-store" });
        if (!r.ok) return;
        const b = await r.json();
        // Belt-and-braces: the server already drops no-language
        // projects, but an older backend (desktop mirror) may still
        // send the meaningless "unspecified" bucket — never show it.
        if (alive)
          setLangs(
            (b.languages || []).filter(
              (l: { clips: number; language: string }) => l.clips > 0 && l.language && l.language !== "unspecified",
            ),
          );
      } catch {
        /* hidden */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!langs || langs.length === 0) return null;

  // "ha" → "Hausa" when the browser knows the code; raw code otherwise.
  const displayName = (code: string) => {
    try {
      const pretty = new Intl.DisplayNames(["en"], { type: "language" }).of(code);
      return pretty && pretty !== code ? pretty : code;
    } catch {
      return code;
    }
  };

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "16px 0 18px" }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-neutral-content-subtler, #64748b)",
        }}
      >
        {t("pages.discover.language_progress")}
      </span>
      {langs.slice(0, 8).map((l) => (
        <span
          key={l.language}
          title={`${l.projects} ${t("pages.discover.projects_lc")}, ${l.validated} ${t("pages.discover.validated_lc")}`}
          style={{
            fontSize: 12,
            padding: "4px 10px",
            borderRadius: 999,
            border: "1px solid var(--color-neutral-border, #cbd5e1)",
            background: "var(--color-neutral-background, #fff)",
          }}
        >
          <strong>{displayName(l.language)}</strong>, {l.clips.toLocaleString()} {t("pages.discover.clips_lc")}
          {(l.hours ?? 0) > 0 ? `, ${l.hours} ${t("pages.discover.hours_abbr")}` : ""}
          {l.validated > 0 ? ` (${Math.round((l.validated / l.clips) * 100)}% ✓)` : ""}
        </span>
      ))}
    </div>
  );
};

export const DiscoverPage = ({
  embedded = false,
  view = "grid",
}: {
  embedded?: boolean;
  view?: "grid" | "list";
} = {}) => {
  // When embedded this only renders while the Discover tab is active, so
  // setting the title to "Discover" is correct in both modes.
  useUpdatePageTitle("Discover");
  const api = useAPI();
  const { user } = useAuth();
  const [projects, setProjects] = useState<DiscoverProject[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState<"" | "open" | "members_only">("");

  // Page-based pagination (mirrors the Projects tab): each page REPLACES
  // the list, and ``count`` drives the shared Pagination control + its
  // per-page selector.
  const load = useCallback(
    async (pageNum: number, size: number, params: { q?: string; mode?: string }) => {
      setLoading(true);
      setError(null);
      try {
        const res = (await api.callApi("discover", {
          params: {
            page: pageNum,
            page_size: size,
            ...(params.q ? { q: params.q } : {}),
            ...(params.mode ? { mode: params.mode } : {}),
          },
          suppressError: true,
        })) as DiscoverResponse | null;
        if (!res) {
          setError("Couldn't reach the Discover feed. Check your connection and try again.");
          return;
        }
        const list = Array.isArray(res) ? res : (res.results ?? []);
        setProjects(list);
        setTotalItems(Array.isArray(res) ? list.length : (res.count ?? list.length));
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  // Reset to page 1 + reload whenever the filters change. Debounced by
  // 300ms so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const h = window.setTimeout(() => {
      setPage(1);
      load(1, pageSize, { q, mode });
    }, 300);
    return () => window.clearTimeout(h);
  }, [q, mode, pageSize, load]);

  // Re-sync when the page is restored from the browser's back/forward
  // cache (bfcache). Joining hard-navigates to the project page (web:
  // ``spaNavigate`` → ``location.href``), so clicking Back restores this
  // page from a frozen snapshot without re-running the load effect —
  // ``already_joined`` would be stale and the card would show "Join"
  // again until a manual refresh. ``pageshow(persisted)`` fires exactly
  // on a bfcache restore; refetch the current page/filters there. Mirrors
  // the same guard ConsentGate uses.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) load(page, pageSize, { q, mode });
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [load, page, pageSize, q, mode]);

  // Join + opt-out both go through a consent modal. These hold the
  // project whose modal is open (null = closed).
  const [joinTarget, setJoinTarget] = useState<DiscoverProject | null>(null);
  const [leaveTarget, setLeaveTarget] = useState<DiscoverProject | null>(null);

  // Facilitator invite deep-link (Phase F): /discover?invite=<token>
  // resolves the token and opens the join modal for that project with
  // the token attached, so the join is attributed to the facilitator.
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("invite");
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/invites/${encodeURIComponent(token)}/`, {
          credentials: "same-origin",
          cache: "no-store",
        });
        if (!r.ok || !alive) return;
        const b = await r.json();
        if (b?.project && !b.project.already_joined && !b.project.already_member) {
          setInviteToken(token);
          setJoinTarget(b.project as DiscoverProject);
        }
      } catch {
        /* dead link — the regular Discover grid still works */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleJoined = useCallback((projectId: number, status?: "active" | "pending") => {
    setJoinTarget(null);
    // Review-mode join: the request is pending, no access yet. Flip the card
    // to a "Requested" state and stay on Discover instead of navigating into
    // a project the user can't open.
    if (status === "pending") {
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, join_status: "pending" } : p)));
      return;
    }
    // Optimistically flip the card to "joined" BEFORE navigating. On web,
    // ``spaNavigate`` is a hard navigation, so the browser bfcaches this
    // page's current DOM/state when we leave. Without this update the
    // cached snapshot still says ``already_joined: false`` and clicking
    // Back shows "Join" again (re-opening the join modal) until a manual
    // refresh. Updating state first means the bfcached snapshot is correct.
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, already_joined: true, contributor_count: p.contributor_count + 1 } : p,
      ),
    );
    // Straight into the project — that's the point of joining.
    spaNavigate(`/projects/${projectId}`);
  }, []);

  const handleLeft = useCallback((projectId: number) => {
    setLeaveTarget(null);
    // Optimistic local update so the card flips back to "Join" without a
    // refetch round trip.
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, already_joined: false, contributor_count: Math.max(0, p.contributor_count - 1) }
          : p,
      ),
    );
  }, []);

  const empty = !loading && projects.length === 0 && !error;
  const displayName = user?.first_name || user?.email?.split("@")[0] || "there";

  // The toolbar + grid body. Rendered standalone (with the projects-page
  // header) at /discover, and embedded (no header/container) as the
  // "Discover" tab on the Projects page — see ``embedded`` below.
  const body = (
    // ``content`` wrapper with the toolbar directly before the list — the
    // same structure as the Projects tab, so the ``toolbar + list``
    // adjacency rule tightens the gap identically. The toolbar stays
    // mounted (filters always usable) even while empty/loading.
    <div className={cn("projects-page").elem("content").toClassName()}>
      <LanguageProgressStrip />
      {/* Filter toolbar — chip filters on the left, search on the right. */}
      <div className={cn("projects-page").elem("toolbar").toClassName()}>
        <div className={cn("projects-page").elem("ws-filter").toClassName()}>
          {[
            { key: "", label: "All projects" },
            { key: "open", label: "Open to join" },
            { key: "members_only", label: "Members only" },
          ].map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              className={cn("projects-page")
                .elem("ws-chip")
                .mod({ active: mode === f.key })
                .toClassName()}
              onClick={() => setMode(f.key as "" | "open" | "members_only")}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ flex: "1 1 auto" }} aria-hidden />
        <input
          className={cn("project-members").elem("search").toClassName()}
          type="search"
          placeholder="Search projects…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ height: 32, minWidth: 200 }}
        />
      </div>

      {error && (
        <div role="alert" style={{ marginBottom: 16 }}>
          <Message variant="negative" size="small">
            {error}
          </Message>
        </div>
      )}

      {empty && (
        <div className={cn("empty-projects-page").toClassName()}>
          <h1 className={cn("empty-projects-page").elem("header").toClassName()}>No public projects yet</h1>
          <p>Check back soon. The platform owner may publish one.</p>
        </div>
      )}

      {loading && projects.length === 0 && (
        <div className={cn("projects-page").elem("loading").toClassName()}>Loading…</div>
      )}

      {projects.length > 0 && (
        <>
          <div className={cn("projects-page").elem("list").mod({ view }).toClassName()}>
            {projects.map((p) => (
              <DiscoverCard
                key={p.id}
                project={p}
                view={view}
                onJoin={() => setJoinTarget(p)}
                onLeave={() => setLeaveTarget(p)}
              />
            ))}
          </div>

          <div className={cn("projects-page").elem("pages").toClassName()}>
            <Pagination
              name="discover-list"
              label="Projects"
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              urlParamName="dpage"
              pageSizeOptions={[10, 20, 30, 50, 100]}
              onPageLoad={(p: number, ps: number) => {
                setPage(p);
                setPageSize(ps);
                load(p, ps, { q, mode });
              }}
            />
          </div>
        </>
      )}

      {joinTarget && (
        <JoinProjectModal
          project={joinTarget}
          onClose={() => setJoinTarget(null)}
          onJoined={handleJoined}
          inviteToken={inviteToken ?? undefined}
        />
      )}
      {leaveTarget && (
        <LeaveProjectModal project={leaveTarget} onClose={() => setLeaveTarget(null)} onLeft={handleLeft} />
      )}
    </div>
  );

  // Embedded as the Projects page's "Discover" tab: the parent already
  // supplies the projects-page container + header, so render only body.
  if (embedded) return body;

  // Standalone /discover route: wrap in the projects-page container +
  // header so it reads like the Projects page.
  return (
    <div className={cn("projects-page").toClassName()}>
      <header className={cn("projects-page").elem("header").toClassName()}>
        <div className={cn("projects-page").elem("header-text").toClassName()}>
          <h1 className={cn("projects-page").elem("header-title").toClassName()}>Discover</h1>
          <p className={cn("projects-page").elem("header-subtitle").toClassName()}>
            Hi {displayName} — public annotation projects you can help with. Join one to start contributing.
          </p>
        </div>
      </header>
      {body}
    </div>
  );
};

/**
 * A single Discover project rendered with the Projects page's
 * ``project-card`` classes so it's visually identical to a project card,
 * with the crowdsource affordances (Join / Open / Members-only) in place
 * of the manager publish pill.
 */
// Fixed width so all card action buttons (Join / Open / Opt out /
// Members only) line up at the same width regardless of label length.
const RATE_SHORT: Record<string, string> = {
  annotation_accepted: "annotation",
  vote_cast: "vote",
  prompt_accepted: "prompt",
  clip_validated: "clip",
};

const fmtRateLine = (card: { currency: string; rates: Record<string, number> }) =>
  Object.entries(card.rates)
    .map(([k, v]) => `${v} ${card.currency || ""}/${RATE_SHORT[k] ?? k}`.replace("  ", " "))
    .join(", ");

// One terse figure for the card face — the highest rate, "+n" when
// more kinds are rated (full list lives in the tooltip).
const fmtTopRate = (card: { currency: string; rates: Record<string, number> }) => {
  const entries = Object.entries(card.rates).sort((a, b) => b[1] - a[1]);
  const [k, v] = entries[0];
  const top = `${v} ${card.currency || ""}/${RATE_SHORT[k] ?? k}`.replace("  ", " ");
  return entries.length > 1 ? `${top} +${entries.length - 1}` : top;
};

const ACTION_BTN = { minWidth: 72, justifyContent: "center" as const };

const DiscoverCard = ({
  project: p,
  onJoin,
  onLeave,
  view = "grid",
}: {
  project: DiscoverProject;
  onJoin: () => void;
  onLeave: () => void;
  view?: "grid" | "list";
}) => {
  const { t } = useTranslation();
  // Joining mutates cloud-owned state — no offline join (the POST is
  // not outbox-queueable by design). Offline, the last-seen feed still
  // renders from cache but the Join affordance is disabled.
  const online = useOnlineStatus();
  // ``already_member`` = org member (opens the project directly, no
  // contributor row to opt out of). ``already_joined`` = joined as a
  // contributor (can opt out). ``joined`` covers both for the "Open" state.
  const isContributor = p.already_joined && !p.already_member;
  const joined = p.already_joined || p.already_member;
  // A review-mode join request awaiting approval: no access yet, and the
  // card should read "Requested" rather than re-open the join modal.
  const pending = p.join_status === "pending";
  // An unjoined user can't open the project page — it 403/404s ("project
  // not found"). So clicking such a card opens the Join modal instead of
  // navigating. Only joined users / members navigate straight in.
  const canJoin = !joined && !pending && p.contribution_mode === "open" && online;
  let created = "";
  try {
    created =
      p.created_at && !Number.isNaN(new Date(p.created_at).getTime())
        ? format(new Date(p.created_at), "dd MMM yyyy, HH:mm")
        : "";
  } catch {
    created = "";
  }

  const linkClass = cn("projects-page").elem("link").mod({ view }).toClassName();

  // The action button cluster is shared between the grid card and the
  // list row — declared once so both layouts stay in lockstep.
  const actionButtons = (
    <div
      style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {joined ? (
        <>
          <Button
            look="outlined"
            variant="primary"
            size="small"
            style={ACTION_BTN}
            onClick={() => spaNavigate(`/projects/${p.id}`)}
          >
            Open
          </Button>
          {isContributor && (
            <Button look="outlined" variant="negative" size="small" style={ACTION_BTN} onClick={onLeave}>
              Opt out
            </Button>
          )}
        </>
      ) : pending ? (
        <Tooltip
          title={t(
            "pages.discover.requested_tooltip",
            undefined,
            "Your request is waiting for the owner to approve it.",
          )}
        >
          <Button look="outlined" variant="neutral" size="small" style={ACTION_BTN} disabled>
            {t("pages.discover.requested", undefined, "Requested")}
          </Button>
        </Tooltip>
      ) : p.contribution_mode === "open" ? (
        online ? (
          <Button look="outlined" variant="primary" size="small" style={ACTION_BTN} onClick={onJoin}>
            {t("pages.discover.join", undefined, "Join")}
          </Button>
        ) : (
          <Tooltip
            title={t(
              "pages.discover.join_offline_tooltip",
              undefined,
              "Joining needs a connection. Try again when you're back online.",
            )}
          >
            <Button look="outlined" variant="neutral" size="small" style={ACTION_BTN} disabled>
              {t("pages.discover.join", undefined, "Join")}
            </Button>
          </Tooltip>
        )
      ) : (
        <Button look="outlined" variant="neutral" size="small" style={ACTION_BTN} disabled>
          Members only
        </Button>
      )}
    </div>
  );

  const metaText = `${created}${p.organization?.title ? `, ${p.organization.title}` : ""}`;
  let createdShort = "";
  try {
    createdShort =
      p.created_at && !Number.isNaN(new Date(p.created_at).getTime())
        ? format(new Date(p.created_at), "dd MMM yyyy")
        : "";
  } catch {
    createdShort = "";
  }

  // List view — one columnar row per project, mirroring the My-projects
  // list: a fixed grid template so title / stats / meta / action line up
  // ACROSS rows (independent flex rows never align). Description is
  // dropped, same as the My-projects condensed view.
  const inner =
    view === "list" ? (
      <div className={cn("project-card").mod({ view }).toClassName()} style={{ width: "100%" }}>
        <div className="discover-list-row">
          <LogoThumb src={p.logo} title={p.title} />
          <Tooltip title={p.title ?? "Untitled project"}>
            <div className="discover-list-row__title">{p.title ?? "Untitled project"}</div>
          </Tooltip>
          <div
            className="discover-list-row__cell discover-list-row__cell_num"
            title={`${p.task_count.toLocaleString()} task${p.task_count === 1 ? "" : "s"}`}
          >
            <IconDocument className="discover-list-row__icon" aria-hidden />
            {p.task_count.toLocaleString()}
          </div>
          <div
            className="discover-list-row__cell discover-list-row__cell_num"
            title={`${p.contributor_count.toLocaleString()} contributor${p.contributor_count === 1 ? "" : "s"}`}
          >
            <IconPeople className="discover-list-row__icon" aria-hidden />
            {p.contributor_count.toLocaleString()}
          </div>
          <div className="discover-list-row__cell discover-list-row__cell_date" title={created}>
            {createdShort}
          </div>
          <div className="discover-list-row__cell discover-list-row__cell_org" title={p.organization?.title ?? ""}>
            <IconGlobe className="discover-list-row__icon" aria-hidden />
            <span className="discover-list-row__org-name">{p.organization?.title ?? "—"}</span>
          </div>
          <div className="discover-list-row__actions">{actionButtons}</div>
        </div>
      </div>
    ) : (
      <div className={cn("project-card").mod({ view }).toClassName()}>
        <div
          className={cn("project-card").elem("header").toClassName()}
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <LogoThumb src={p.logo} title={p.title} />
          <div className={cn("project-card").elem("title").toClassName()} style={{ minWidth: 0 }}>
            <div className={cn("project-card").elem("title-text-wrapper").toClassName()}>
              <Tooltip title={p.title ?? "Untitled project"}>
                <div className={cn("project-card").elem("title-text").toClassName()}>
                  {p.title ?? "Untitled project"}
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        {/* Stats sit right under the title, inset + styled to match the
            description line below them (same 16px padding, caption size). */}
        <div
          className={cn("project-card").elem("stats").toClassName()}
          style={{
            display: "flex",
            gap: 8,
            padding: "10px 16px 0",
            fontSize: "var(--font-size-caption)",
            lineHeight: "18px",
            color: "var(--color-neutral-content-subtle)",
          }}
        >
          <span>{p.task_count.toLocaleString()} tasks</span>
          <span aria-hidden>,</span>
          <span>
            {p.contributor_count.toLocaleString()} contributor{p.contributor_count === 1 ? "" : "s"}
          </span>
        </div>
        {/* Posted rates on their own row under the stats (owner:
            keeps the stats line tight). Height reserved even when
            unrated so dates/buttons still align across the grid. */}
        <div
          style={{
            padding: "2px 16px 0",
            fontSize: "var(--font-size-caption)",
            lineHeight: "18px",
            minHeight: 18,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "var(--color-positive-content, #1f7a5a)",
            fontWeight: 600,
          }}
          title={p.rate_card?.rates ? `Owner-declared rates: ${fmtRateLine(p.rate_card)}` : undefined}
        >
          {p.rate_card?.rates && Object.keys(p.rate_card.rates).length > 0 ? `Pays ${fmtRateLine(p.rate_card)}` : ""}
        </div>
        {/* Description in the middle — clamped to 2 lines with an ellipsis;
            reserves those 2 lines even when empty so every card's date /
            buttons line up across the grid. */}
        <div
          className={cn("project-card").elem("description").toClassName()}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "2.9em",
            color: p.description ? undefined : "var(--color-neutral-content-subtler)",
            fontStyle: p.description ? undefined : "italic",
          }}
          title={p.description ?? ""}
        >
          {p.description || "No description provided."}
        </div>
        {/* Actions live on the card's bottom row (not the title row) so a
            long title can't collide with them. Date + org sit on the left,
            the action button(s) hug the right. */}
        <div className={cn("project-card").elem("info").toClassName()}>
          <div
            className={cn("project-card").elem("created-date").toClassName()}
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={metaText}
          >
            {metaText}
          </div>
          {actionButtons}
        </div>
      </div>
    );
  // Joined / member → navigate into the project.
  if (joined) {
    return (
      <NavLink className={linkClass} to={`/projects/${p.id}`} data-external>
        {inner}
      </NavLink>
    );
  }
  // Open + unjoined → clicking the card opens the Join modal.
  if (canJoin) {
    return (
      <div
        className={linkClass}
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer" }}
        onClick={onJoin}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onJoin();
          }
        }}
      >
        {inner}
      </div>
    );
  }
  // Members-only + not a member → inert card (the disabled "Members only"
  // button is the only affordance).
  return (
    <div className={linkClass} style={{ cursor: "default" }}>
      {inner}
    </div>
  );
};

// Route metadata consumed by the Pages registry (see pages/index.js +
// routeHelpers.resolveRoutes). Same pattern as HomePage.
(DiscoverPage as unknown as { title: string; path: string; exact: boolean }).title = "Discover";
(DiscoverPage as unknown as { title: string; path: string; exact: boolean }).path = "/discover";
(DiscoverPage as unknown as { title: string; path: string; exact: boolean }).exact = true;

export default DiscoverPage;
